import { useMemo } from 'react'

import { TeamApiService, type TeamMemberDto } from '@api'
import type { UseQueryOptions } from 'react-query'
import { useQuery } from 'react-query'

export const useFetchTeamTable = (
  queryOptions?: UseQueryOptions<TeamMemberDto[], unknown>,
) => {
  const { data, isFetching, error } = useQuery<TeamMemberDto[]>(
    'team',
    TeamApiService.listMembers,
    queryOptions,
  )

  // Sort the fetched data by roles
  const sortedData = useMemo(() => _sortMembersByRole(data), [data])

  return useMemo(
    () => ({
      data: sortedData,
      isFetching,
      error,
    }),
    [sortedData, isFetching, error],
  )
}

export const useTeamForReassignment = (
  creatorId: number,
  queryOptions?: UseQueryOptions<TeamMemberDto[], unknown>,
) => {
  const { data, isFetching, error } = useQuery<TeamMemberDto[]>(
    ['team', creatorId],
    () => TeamApiService.listMembersForSalesReassignment({ creatorId }),
    queryOptions,
  )

  // Sort the fetched data by roles
  const sortedData = useMemo(() => _sortMembersByRole(data), [data])

  return useMemo(
    () => ({
      data: sortedData,
      isFetching,
      error,
    }),
    [sortedData, isFetching, error],
  )
}

const _sortMembersByRole = (data: TeamMemberDto[] | undefined) => {
  if (!data) {
    return []
  }

  const roleWeights: Record<string, number> = {
    Leader: 0,
    Admin: 1,
    Member: 2,
  }

  return [...data].sort((a, b) => roleWeights[a.role] - roleWeights[b.role])
}
